import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authAPI } from '../../utils/api';
import { getStorage, getSessionStorage } from 'shared/platform/adapter';
import { decodeJwtPayload } from '../../utils/jwt';

// Важно: при инициализации модулей (hard refresh) adapter может быть ещё не установлен.
// Поэтому используем безопасный fallback на window.localStorage/sessionStorage.
const st = () => {
  const adapterStorage = getStorage();
  if (adapterStorage) return adapterStorage;
  if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  return null;
};
const sess = () => {
  const adapterSessionStorage = getSessionStorage();
  if (adapterSessionStorage) return adapterSessionStorage;
  if (typeof window !== 'undefined' && window.sessionStorage) return window.sessionStorage;
  return null;
};
const getStored = (k) => st()?.getItem(k) ?? null;
const setStored = (k, v) => { const s = st(); if (s) s.setItem(k, v); };
const removeStored = (k) => { const s = st(); if (s) s.removeItem(k); };
const getSess = (k) => sess()?.getItem(k) ?? null;
const setSess = (k, v) => { const s = sess(); if (s) s.setItem(k, v); };
const removeSess = (k) => { const s = sess(); if (s) s.removeItem(k); };

export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      setStored('token', response.data.token);
      if (response.data.refreshToken) {
        setStored('refreshToken', response.data.refreshToken);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Login failed');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authAPI.register(userData);
      setStored('token', response.data.token);
      return response.data;
    } catch (error) {
      const errMsg = error.response?.data?.error || error.response?.data?.details || 'Registration failed';
      return rejectWithValue(errMsg);
    }
  }
);

export const getProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.getProfile();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to get profile');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (updateData, { rejectWithValue }) => {
    try {
      const response = await authAPI.updateProfile(updateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update profile');
    }
  }
);

// Функция для декодирования JWT токена
const decodeToken = (token) => {
  try {
    return decodeJwtPayload(token);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

// Инициализация: если есть токен, декодируем его и восстанавливаем user (вызов при загрузке модуля, adapter уже должен быть установлен)
const getInitialAuthState = () => {
  const token = getStored('token');
  let initialUser = null;
  if (token) {
    const decoded = decodeToken(token);
    if (decoded && decoded.id) {
      initialUser = {
        id: decoded.id,
        email: decoded.email,
        firstName: decoded.first_name || decoded.firstName,
        lastName: decoded.last_name || decoded.lastName,
        isAdmin: decoded.is_admin || decoded.isAdmin || false,
      };
    }
  }
  return {
    user: initialUser,
    token: token,
    // Считаем пользователя аутентифицированным при наличии токена:
    // профиль/поля user догружаются отдельными запросами.
    isAuthenticated: !!token,
    isLoading: false,
    error: null,
    isBanned: getSess('isBanned') === 'true',
  };
};

const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialAuthState(),
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isBanned = false;
      removeStored('token');
      removeStored('refreshToken');
      removeStored('profile_username');
      removeStored('profile_username_userId');
      removeSess('isBanned');
    },
    clearError: (state) => {
      state.error = null;
    },
    clearBanFlag: (state) => {
      state.isBanned = false;
      removeSess('isBanned');
    },
    setCredentials: (state, action) => {
      const { token, user } = action.payload;
      state.token = token;
      state.user = user;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        removeStored('profile_username');
        removeStored('profile_username_userId');
        // Нормализуем данные пользователя из ответа
        const userData = action.payload.user;
        state.user = {
          ...userData,
          isAdmin: userData.is_admin !== undefined ? userData.is_admin : (userData.isAdmin !== undefined ? userData.isAdmin : false),
          firstName: userData.first_name || userData.firstName,
          lastName: userData.last_name || userData.lastName,
          avatarUrl: userData.avatar_url || userData.avatarUrl,
        };
        state.token = action.payload.token;
        // Автоматически загружаем полный профиль после логина для получения актуального аватара
        // Это будет выполнено через dispatch в компоненте или middleware
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        removeStored('profile_username');
        removeStored('profile_username_userId');
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Profile
      .addCase(getProfile.pending, (state) => {
        state.isLoading = true;
        // НЕ сбрасываем isBanned при начале загрузки профиля
        // Только явно через clearBanFlag или logout
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        // Нормализуем данные из бэкенда (snake_case -> camelCase)
        const payload = action.payload;
        
        // Проверяем, забанен ли пользователь
        const isBanned = payload.is_banned !== undefined ? payload.is_banned : (payload.isBanned !== undefined ? payload.isBanned : false);
        if (isBanned) {
          state.isBanned = true;
          setSess('isBanned', 'true');
        } else {
          removeSess('isBanned');
        }
        
        // Определяем username: если в payload он явно указан (даже как пустая строка), используем его
        // Иначе сохраняем старое значение из state.user
        let username = '';
        if ('username' in payload || payload.username !== undefined) {
          // Поле явно присутствует в ответе - используем его значение (даже если это пустая строка или null)
          username = payload.username !== null && payload.username !== undefined ? String(payload.username) : '';
          // console.log('✅ authSlice: Using username from payload:', username);
        } else {
          // Поле отсутствует в ответе - сохраняем старое значение
          username = state.user?.username || '';
          // console.log('⚠️ authSlice: username not in payload, keeping old value:', username);
        }
        
        // Обновляем данные пользователя только если он не забанен
        // Если забанен, мы уже установили флаг, и не обновляем данные пользователя
        if (!isBanned) {
          state.user = {
            id: payload.id || state.user?.id,
            email: payload.email || state.user?.email,
            username: username,
            firstName: payload.first_name || payload.firstName || state.user?.firstName || '',
            lastName: payload.last_name || payload.lastName || state.user?.lastName || '',
            first_name: payload.first_name || payload.firstName || state.user?.first_name,
            last_name: payload.last_name || payload.lastName || state.user?.last_name,
            avatarUrl: payload.avatar_url !== undefined ? (payload.avatar_url || '') : (payload.avatarUrl !== undefined ? (payload.avatarUrl || '') : (state.user?.avatarUrl || '')),
            avatar_url: payload.avatar_url !== undefined ? (payload.avatar_url || '') : (payload.avatarUrl !== undefined ? (payload.avatarUrl || '') : state.user?.avatar_url),
            avatarHidden: payload.avatar_hidden !== undefined ? payload.avatar_hidden : (payload.avatarHidden !== undefined ? payload.avatarHidden : (state.user?.avatarHidden !== undefined ? state.user?.avatarHidden : false)),
            avatar_hidden: payload.avatar_hidden !== undefined ? payload.avatar_hidden : (payload.avatarHidden !== undefined ? payload.avatarHidden : (state.user?.avatar_hidden !== undefined ? state.user?.avatar_hidden : false)),
            bio: payload.bio !== undefined ? payload.bio : (state.user?.bio || ''),
            publicEmail: payload.public_email !== undefined ? payload.public_email : (payload.publicEmail !== undefined ? payload.publicEmail : (state.user?.publicEmail || false)),
            public_email: payload.public_email !== undefined ? payload.public_email : (payload.publicEmail !== undefined ? payload.publicEmail : state.user?.public_email),
            publicContacts: payload.public_contacts !== undefined ? payload.public_contacts : (payload.publicContacts !== undefined ? payload.publicContacts : (state.user?.publicContacts || false)),
            public_contacts: payload.public_contacts !== undefined ? payload.public_contacts : (payload.publicContacts !== undefined ? payload.publicContacts : state.user?.public_contacts),
            allowDmNonFriends: payload.allow_dm_non_friends !== undefined ? payload.allow_dm_non_friends : (payload.allowDmNonFriends !== undefined ? payload.allowDmNonFriends : (state.user?.allowDmNonFriends !== undefined ? state.user.allowDmNonFriends : true)),
            allow_dm_non_friends: payload.allow_dm_non_friends !== undefined ? payload.allow_dm_non_friends : (payload.allowDmNonFriends !== undefined ? payload.allowDmNonFriends : state.user?.allow_dm_non_friends),
            city: payload.city !== undefined ? payload.city : (state.user?.city || ''),
            defaultLat: payload.default_lat !== undefined ? payload.default_lat : (payload.defaultLat !== undefined ? payload.defaultLat : state.user?.defaultLat),
            default_lat: payload.default_lat !== undefined ? payload.default_lat : (payload.defaultLat !== undefined ? payload.defaultLat : state.user?.default_lat),
            defaultLng: payload.default_lng !== undefined ? payload.default_lng : (payload.defaultLng !== undefined ? payload.defaultLng : state.user?.defaultLng),
            default_lng: payload.default_lng !== undefined ? payload.default_lng : (payload.defaultLng !== undefined ? payload.defaultLng : state.user?.default_lng),
            createdAt: payload.created_at || payload.createdAt || state.user?.createdAt,
            created_at: payload.created_at || payload.createdAt || state.user?.created_at,
            isAdmin: payload.is_admin !== undefined ? payload.is_admin : (payload.isAdmin !== undefined ? payload.isAdmin : (state.user?.isAdmin || false)),
            friends_count: payload.friends_count !== undefined ? payload.friends_count : (payload.friendsCount !== undefined ? payload.friendsCount : state.user?.friends_count || 0),
            friendsCount: payload.friends_count !== undefined ? payload.friends_count : (payload.friendsCount !== undefined ? payload.friendsCount : state.user?.friendsCount || 0),
            followers_count: payload.followers_count !== undefined ? payload.followers_count : (payload.followersCount !== undefined ? payload.followersCount : state.user?.followers_count || 0),
            followersCount: payload.followers_count !== undefined ? payload.followers_count : (payload.followersCount !== undefined ? payload.followersCount : state.user?.followersCount || 0),
            following_count: payload.following_count !== undefined ? payload.following_count : (payload.followingCount !== undefined ? payload.followingCount : state.user?.following_count || 0),
            followingCount: payload.following_count !== undefined ? payload.following_count : (payload.followingCount !== undefined ? payload.followingCount : state.user?.followingCount || 0),
          };
          
          // console.log('✅ authSlice: Updated state.user.username to:', state.user.username);
        }
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.isLoading = false;
        // Не сбрасываем аутентификацию сразу - возможно, токен просто истек, но может быть обновлен через interceptor
        // Только если это явная ошибка 401 (Unauthorized) или ошибка говорит о недействительности токена
        const error = action.payload || '';
        const errorString = typeof error === 'string' ? error : '';
        
        // Проверяем, не связана ли ошибка с баном
        if (errorString.toLowerCase().includes('banned') ||
            errorString.toLowerCase().includes('заблокирован') ||
            errorString.toLowerCase().includes('забанен') ||
            errorString.toLowerCase().includes('account is banned')) {
          state.isBanned = true;
          setSess('isBanned', 'true');
          return;
        }
        
        // Не сбрасываем при временных/сетевых ошибках (рестарт бэка, 502/503)
        const isTemporaryError = /502|503|504|Network Error|timeout|ECONNREFUSED|ETIMEDOUT/i.test(errorString);
        if (isTemporaryError) {
          return; // Оставляем пользователя залогиненным, токен восстановится при следующем запросе
        }
        // Не сбрасываем при "Refresh token invalid" — api уже очистил токены и сделал редирект на логин
        if (/refresh token invalid|Refresh token invalid/i.test(errorString)) {
          return;
        }
        // Сбрасываем только при явных ошибках аутентификации (401, невалидный/истёкший токен)
        if (errorString.includes('401') ||
            errorString.includes('Unauthorized') ||
            errorString.includes('invalid token') ||
            errorString.includes('token expired')) {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
          removeStored('token');
          removeStored('refreshToken');
        }
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        // Бэкенд возвращает обновленные данные в формате snake_case
        // Нормализуем данные для совместимости
        const payload = action.payload;
        state.user = {
          ...state.user,
          // Обновляем все поля, учитывая оба формата
          id: payload.id || state.user?.id,
          email: payload.email || state.user?.email,
          username: payload.username !== undefined && payload.username !== null ? payload.username : (state.user?.username || ''),
          firstName: payload.first_name || payload.firstName || state.user?.firstName || '',
          lastName: payload.last_name || payload.lastName || state.user?.lastName || '',
          first_name: payload.first_name || payload.firstName || state.user?.first_name,
          last_name: payload.last_name || payload.lastName || state.user?.last_name,
          avatarUrl: payload.avatar_url !== undefined ? (payload.avatar_url || '') : (payload.avatarUrl !== undefined ? (payload.avatarUrl || '') : (state.user?.avatarUrl || '')),
          avatar_url: payload.avatar_url !== undefined ? (payload.avatar_url || '') : (payload.avatarUrl !== undefined ? (payload.avatarUrl || '') : state.user?.avatar_url),
          avatarHidden: payload.avatar_hidden !== undefined ? payload.avatar_hidden : (payload.avatarHidden !== undefined ? payload.avatarHidden : (state.user?.avatarHidden !== undefined ? state.user?.avatarHidden : false)),
          avatar_hidden: payload.avatar_hidden !== undefined ? payload.avatar_hidden : (payload.avatarHidden !== undefined ? payload.avatarHidden : (state.user?.avatar_hidden !== undefined ? state.user?.avatar_hidden : false)),
          bio: payload.bio !== undefined ? payload.bio : (state.user?.bio || ''),
          publicEmail: payload.public_email !== undefined ? payload.public_email : (payload.publicEmail !== undefined ? payload.publicEmail : state.user?.publicEmail),
          public_email: payload.public_email !== undefined ? payload.public_email : (payload.publicEmail !== undefined ? payload.publicEmail : state.user?.public_email),
          publicContacts: payload.public_contacts !== undefined ? payload.public_contacts : (payload.publicContacts !== undefined ? payload.publicContacts : state.user?.publicContacts),
          public_contacts: payload.public_contacts !== undefined ? payload.public_contacts : (payload.publicContacts !== undefined ? payload.publicContacts : state.user?.public_contacts),
          allowDmNonFriends: payload.allow_dm_non_friends !== undefined ? payload.allow_dm_non_friends : (payload.allowDmNonFriends !== undefined ? payload.allowDmNonFriends : state.user?.allowDmNonFriends),
          allow_dm_non_friends: payload.allow_dm_non_friends !== undefined ? payload.allow_dm_non_friends : (payload.allowDmNonFriends !== undefined ? payload.allowDmNonFriends : state.user?.allow_dm_non_friends),
          city: payload.city !== undefined ? payload.city : (state.user?.city || ''),
          defaultLat: payload.default_lat !== undefined ? payload.default_lat : (payload.defaultLat !== undefined ? payload.defaultLat : state.user?.defaultLat),
          default_lat: payload.default_lat !== undefined ? payload.default_lat : (payload.defaultLat !== undefined ? payload.defaultLat : state.user?.default_lat),
          defaultLng: payload.default_lng !== undefined ? payload.default_lng : (payload.defaultLng !== undefined ? payload.defaultLng : state.user?.defaultLng),
          default_lng: payload.default_lng !== undefined ? payload.default_lng : (payload.defaultLng !== undefined ? payload.defaultLng : state.user?.default_lng),
          createdAt: payload.created_at || payload.createdAt || state.user?.createdAt,
          created_at: payload.created_at || payload.createdAt || state.user?.created_at,
        };
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Экспортируем все actions и thunks
export const { logout, clearError, clearBanFlag, setCredentials } = authSlice.actions;

// Селекторы
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;

export default authSlice.reducer;